/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { useCallback, useEffect, useState } from "react";

import { BlogTagFragment, CosmosVideoEmbedFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { IFrame } from "./IFrame.atom";
import { CosmosPlayIcon, CosmosTagProps, CosmosTagsAndAttribution } from "@components/elements";
import { isHttpsUrl } from "@cosmos/common-utils";
import { useAssetOptUrl, useIsMobile } from "@components/hooks";

export interface CosmosEmbedProps extends Omit<CosmosVideoEmbedFragment, "__typename"> {
	className?: string;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	attributionClassName?: string;
}

enum EmbedVariantEnum {
	Miscellaneous = "miscellaneous",
	Prohibited = "prohibited",
	Vimeo = "vimeo",
	YouTube = "youtube",
}

const styles = css({
	position: "relative",
	overflow: "hidden",
	aspectRatio: "16/9",
	borderRadius: "eds.dimension.24",
});

const getFinalIframeSrc = (embedVariant: EmbedVariantEnum, iframeSrc?: string | null, mute?: boolean): string => {
	if (!iframeSrc || !isHttpsUrl(iframeSrc)) return "";
	const iframeUrl = new URL(iframeSrc);

	switch (embedVariant) {
		case EmbedVariantEnum.Vimeo: {
			iframeUrl.searchParams.set("dnt", "true");
			iframeUrl.searchParams.set("autoplay", "1");
			if (mute) {
				iframeUrl.searchParams.set("muted", "1");
			}

			return iframeUrl.toString();
		}
		case EmbedVariantEnum.YouTube: {
			if (iframeUrl.searchParams.get("v") && iframeUrl.pathname === "/watch") {
				const id = iframeUrl.searchParams.get("v");
				iframeUrl.pathname = `/embed/${id}`;
				iframeUrl.searchParams.delete("v");
			}

			iframeUrl.searchParams.set("rel", "0");
			iframeUrl.searchParams.set("autoplay", "1");
			if (mute) {
				iframeUrl.searchParams.set("mute", "1");
			}

			let finalIframeSrc = iframeUrl.toString().replace(/youtube\.com/gi, "youtube-nocookie.com");
			finalIframeSrc = finalIframeSrc.replace(/youtu\.be/gi, "www.youtube-nocookie.com/embed");
			return finalIframeSrc;
		}
		default:
			return iframeSrc;
	}
};

export const CosmosEmbed = ({
	className,
	iframeSrc,
	playButtonAriaLabel,
	overlayImage,
	disableOverlay,
	tags,
	attribution,
	attributionClassName,
}: CosmosEmbedProps): React.JSX.Element | null => {
	const isMobile = useIsMobile();
	const shouldMute = (!isMobile && disableOverlay) || false;
	const [isPlayClicked, setIsPlayClicked] = useState(disableOverlay || false);
	const [overlayImageUrl, setOverlayImageUrl] = useState("");
	const overlayOptUrl = useAssetOptUrl(overlayImage);
	let embedVariant = EmbedVariantEnum.Miscellaneous;
	if (iframeSrc) {
		if (/(bilibili\.com|twitch\.tv)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.Prohibited;
		if (/(player\.vimeo\.com)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.Vimeo;
		if (/(youtu\.be|youtube\.com|youtube-nocookie\.com)/g.test(iframeSrc)) embedVariant = EmbedVariantEnum.YouTube;
	}
	const finalIframeSrc = getFinalIframeSrc(embedVariant, iframeSrc, shouldMute);

	const setYtThumbnail = useCallback(() => {
		// create an Image object to check if the maxresdefault thumbnail exists
		const ytContentId = finalIframeSrc.replace(/.*\/embed\/([^?]+).*/, "$1");
		const img = new Image();
		const maxResYtThumbnail = `https://img.youtube.com/vi/${ytContentId}/maxresdefault.jpg`;
		const fallbackYtThumbnail = `https://img.youtube.com/vi/${ytContentId}/hqdefault.jpg`;
		img.src = maxResYtThumbnail;
		// if maxresdefault loads, use it; otherwise fall back to hqdefault
		img.onload = () => {
			// accommodate some browsers which still fire the onload event for images that 404
			if (img.width > 120) {
				// maxresdefault returns a tiny 120x90 px image on 404
				setOverlayImageUrl(maxResYtThumbnail);
			} else {
				// set fallback
				setOverlayImageUrl(fallbackYtThumbnail);
			}
		};
		img.onerror = () => {
			setOverlayImageUrl(fallbackYtThumbnail);
		};
	}, [finalIframeSrc, setOverlayImageUrl]);

	const handlePlayClick = useCallback((): void => {
		// force prohibited iframe source urls to open in a new tab
		if (embedVariant === EmbedVariantEnum.Prohibited) {
			window.open(finalIframeSrc, "_blank");
			return;
		}
		setIsPlayClicked(true);
	}, [embedVariant, finalIframeSrc, setIsPlayClicked]);

	useEffect(() => {
		if (overlayOptUrl) {
			setOverlayImageUrl(overlayOptUrl);
		} else if (embedVariant === EmbedVariantEnum.YouTube) {
			setYtThumbnail();
		}
	}, [overlayOptUrl, setYtThumbnail, embedVariant]);

	if (!iframeSrc) return null;

	return (
		<div className={cx("cosmos-video-embed", styles, className)}>
			{isPlayClicked ? (
				<IFrame iframeSrc={finalIframeSrc} title={playButtonAriaLabel} />
			) : (
				<button
					className={css({
						cursor: "pointer",
						width: "100%",
						height: "100%",
						position: "absolute",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						bgRepeat: "no-repeat",
						bgPosition: "top",
						bgSize: "cover",
						bgBlendMode: "darken",
						bgColor: "eds.background.backdrop.regular",
						"&:hover .cosmos-play-icon": {
							background: "eds.fill.secondary.hover",
						},
						"&:focus-visible": {
							outlineColor: "token(colors.eds.border.focus)",
							outlineOffset: "-eds.dimension.3",
							borderRadius: "eds.borderRadius.16",
						},
					})}
					style={{ backgroundImage: `url(${overlayImageUrl})` }}
					aria-label={playButtonAriaLabel || "Play Video"}
					onClick={handlePlayClick}
				>
					<CosmosTagsAndAttribution className={attributionClassName} tags={tags} attribution={attribution}>
						<CosmosPlayIcon size="lg" />
					</CosmosTagsAndAttribution>
				</button>
			)}
		</div>
	);
};
